import React from 'react';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { useListLinkNavigation } from '../../hooks/useListLinkNavigation';
import type {
	EnhancedFieldConfiguration,
	ListBuilderCallbacks,
} from '../../types';
import { getFieldDisplayProperties } from './displayHelpers';
import { handleTruncationTooltip } from './truncationTooltip';

interface BadgeDisplayProps {
	field: EnhancedFieldConfiguration;
	value: any;
	data: any;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

export function BadgeDisplay({
	field,
	value,
	data,
	listKey,
	objects,
	callbacks,
}: BadgeDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { styleConfig, link } = field;

	// Use the shared link navigation hook
	const { linkUrl, handleLinkClick, isCallback } = useListLinkNavigation({
		field,
		data,
		listKey,
		objects,
		callbacks,
	});

	// Get display value and color using value mapping
	let { displayValue, colorValue } = getFieldDisplayProperties(
		field,
		value,
		data,
	);

	// Layout spacer: an explicitly empty `custom_text` source (as opposed to
	// missing field data) is used to reserve horizontal space so subsequent
	// rows in a multi-line cell stay indented under a `floatingLeft` badge
	// above. Skip the "-" fallback for these and render an invisible
	// placeholder of normal badge size below.
	const isLayoutSpacer =
		field.dataConfig.type === 'custom_text' &&
		(field.dataConfig.customText ?? '') === '';

	if (
		!isLayoutSpacer &&
		(displayValue == null ||
			(typeof displayValue === 'string' && displayValue.trim() === '') ||
			displayValue === '-')
	) {
		displayValue = '-';

		// Check if this is an empty/default value without a color - render as plain text
		if (colorValue == null) {
			return (
				<span style={{ color: colors.values.textSecondary }}>
					{t(displayValue)}
				</span>
			);
		}
	}

	// Helper to get record ID for link enabled check
	const getRecordId = (record: unknown, idField?: string): unknown => {
		if (record == null) {
			return null;
		}

		const rec = record as Record<string, unknown>;

		if (idField != null) {
			return rec[idField] ?? null;
		}

		return (
			rec.id ?? rec._id ?? rec.Id ?? rec.ID ?? rec.clientId ?? rec.client_id
		);
	};

	// Build container classes
	const getSizeClasses = () => {
		switch (styleConfig?.size) {
			case 'xs':
				return 'px-1.5 py-0.5 text-xs';
			case 'sm':
				return 'px-2 py-0.5 text-sm';
			case 'lg':
				return 'px-3 py-1.5 text-base';
			case 'xl':
				return 'px-4 py-2 text-lg';
			default: // md
				return 'px-2.5 py-1 text-xs';
		}
	};

	const getVariantClasses = () => {
		const baseClasses = 'inline-flex items-center font-medium rounded';

		switch (styleConfig?.variant) {
			case 'outline':
				return `${baseClasses} bg-transparent border`;
			case 'ghost':
				return `${baseClasses} bg-transparent`;
			default: // filled
				return baseClasses;
		}
	};

	// Apply color styles with theme-aware resolution
	const getColorStyles = () => {
		const styles: React.CSSProperties = {};

		// Resolve theme-aware colors
		const resolvedBg = colorValue?.bg
			? resolveThemeColor(colorValue.bg, isDark)
			: null;
		const resolvedText = colorValue?.text
			? resolveThemeColor(colorValue.text, isDark)
			: null;
		const resolvedBorder = colorValue?.border
			? resolveThemeColor(colorValue.border, isDark)
			: null;

		if (styleConfig?.variant === 'outline') {
			styles.borderColor =
				resolvedBorder ?? resolvedBg ?? colors.values.borderDefault;
			styles.color = resolvedText ?? colors.values.textPrimary;
		} else if (styleConfig?.variant === 'ghost') {
			styles.color = resolvedText ?? colors.values.textPrimary;
		} else {
			// Filled variant
			styles.backgroundColor = resolvedBg ?? colors.values.bgSecondary;
			styles.color = resolvedText ?? colors.values.textPrimary;
		}

		return styles;
	};

	if (isLayoutSpacer) {
		return <span className="inline-block w-1" aria-hidden="true" />;
	}

	// Check if this field has linking enabled
	const recordId = getRecordId(data, link?.idField);

	const isLinkConfigured =
		link?.enabled &&
		(link.type === 'callback' ||
			link.type === 'custom' ||
			(link.type === 'object' && recordId != null));

	// useListLinkNavigation may suppress the URL (e.g. for client-detail rows
	// the caller can't open). When that happens, render the badge as a plain
	// container — no clickable cursor, no hover state.
	const isLinkEnabled = isLinkConfigured && (isCallback || linkUrl != null);

	const containerClasses = cn(getVariantClasses(), getSizeClasses());
	const containerStyles = getColorStyles();

	if (isLinkEnabled) {
		const badgeContent = (
			<span className="truncate" onMouseEnter={handleTruncationTooltip}>
				{t(String(displayValue))}
			</span>
		);

		if (!isCallback && linkUrl != null) {
			return (
				<a
					href={linkUrl}
					onClick={handleLinkClick}
					className={cn(
						containerClasses,
						'hover:opacity-80 transition-opacity cursor-pointer no-underline',
					)}
					style={containerStyles}
				>
					{badgeContent}
				</a>
			);
		}

		return (
			<button
				onClick={handleLinkClick}
				className={cn(
					containerClasses,
					'hover:opacity-80 transition-opacity cursor-pointer',
				)}
				style={containerStyles}
				type="button"
			>
				{badgeContent}
			</button>
		);
	}

	return (
		<div className={containerClasses} style={containerStyles}>
			<span className="truncate" onMouseEnter={handleTruncationTooltip}>
				{t(String(displayValue))}
			</span>
		</div>
	);
}
